import { Avatar } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import { apiBaseUrl } from '@ayocore/platform/client/helpers/fetcher';
import { getUserInitials } from '@ayocore/platform/client/helpers/userInitials';

/** Размеры аватара: sm для списков/сайдбара, md для карточек, lg для профиля */
type AvatarSize = 'sm' | 'md' | 'lg';

const SIZE_MAP: Record<AvatarSize, number> = {
  sm: 32,
  md: 48,
  lg: 128,
};

export interface UserAvatarProps {
  /** URL миниатюры аватара (64×64) */
  avatarThumbUrl?: string | null;
  /** URL среднего аватара (256×256) */
  avatarMediumUrl?: string | null;
  /** Имя пользователя (для генерации инициалов в фоллбеке) */
  name?: string | null;
  /** Размер аватара */
  size?: AvatarSize;
}

/**
 * UserAvatar — реюзабельный компонент аватара пользователя.
 *
 * - sm (32px) и md (48px) → используют thumb URL
 * - lg (128px) → использует medium URL
 * - Фоллбек: инициалы из name или иконка пользователя
 */
export function UserAvatar({
  avatarThumbUrl,
  avatarMediumUrl,
  name,
  size = 'md',
}: UserAvatarProps) {
  const px = SIZE_MAP[size];

  // lg → medium URL, остальные → thumb
  const rawSrc =
    size === 'lg'
      ? (avatarMediumUrl ?? avatarThumbUrl ?? null)
      : (avatarThumbUrl ?? avatarMediumUrl ?? null);

  // Относительные URL'ы (/uploads/...) дополняем базовым адресом сервера (для dev-режима)
  const src = rawSrc && rawSrc.startsWith('/') ? `${apiBaseUrl}${rawSrc}` : rawSrc;

  // Фоллбек — инициалы или иконка
  if (src) {
    return <Avatar src={src} size={px} radius='xl' alt={name ?? 'Аватар'} />;
  }

  if (name) {
    return (
      <Avatar size={px} radius='xl' color='blue'>
        {getUserInitials(name)}
      </Avatar>
    );
  }

  return (
    <Avatar size={px} radius='xl' color='gray'>
      <IconUser size={px * 0.5} />
    </Avatar>
  );
}
